import { useEffect, useState } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import {
  feedbackImagesOf,
  type FeedbackRecordImage,
} from "@shared-core/domain/feedback-images";
import type { RecordView } from "@shared-core/domain/object-ui/contract";
import { Card, CardContent } from "../../primitives/card";

type Loaded =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "loaded"; blob: Blob; url: string };

/**
 * One image, fetched with the person's sign-in (auth is header-based, so a plain `<img src>` on the route would
 * arrive unauthenticated) and shown from an object URL, which is released when it goes.
 */
function ImageThumbnail({
  image,
  position,
}: {
  image: FeedbackRecordImage;
  position: number;
}) {
  const adapter = useObjectUiAdapter();
  const capabilities = usePlatformCapabilities();
  const [loaded, setLoaded] = useState<Loaded>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoaded({ status: "loading" });
    adapter.download(image.downloadHref).then(
      ({ blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({ status: "loaded", blob, url: objectUrl });
      },
      () => {
        if (!cancelled) setLoaded({ status: "failed" });
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [adapter, image.downloadHref]);

  if (loaded.status === "failed") {
    return (
      <div className="flex h-28 w-40 items-center justify-center rounded-md border bg-muted px-2 text-center text-xs text-muted-foreground">
        Image {position} could not be loaded.
      </div>
    );
  }
  if (loaded.status === "loading") {
    return (
      <div
        role="status"
        className="flex h-28 w-40 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground"
      >
        Loading image {position}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => capabilities.openBlobInNewTab(loaded.blob)}
      aria-label={`Open image ${position} full size`}
      className="overflow-hidden rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <img
        src={loaded.url}
        alt={image.filename}
        className="h-28 w-40 object-cover"
      />
    </button>
  );
}

/**
 * The screenshot and pasted images that came with a note, as thumbnails that open full size. Renders nothing while
 * the record carries no images. One-only: a custom section on `feedback.feedback_item`.
 */
export function FeedbackImagesSection({ record }: { record: RecordView }) {
  const images = feedbackImagesOf(record);
  if (images.length === 0) return null;
  return (
    <Card data-section-id="feedback.images">
      <CardContent className="space-y-3 py-4">
        <h2 className="text-base font-semibold">Images</h2>
        <ul className="flex flex-wrap gap-3">
          {images.map((image, n) => (
            <li key={image.imageId}>
              <ImageThumbnail image={image} position={n + 1} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
